import React, { useEffect, useState } from 'react';
import MasterLayout from '../../layouts/MasterLayout';
import ThemeBreadcrumb from '../../components/themes/ThemeBreadcrumb';
import Swal from "sweetalert2";
import { useDispatch } from 'react-redux';
import { setShowPreloader } from '../../redux/action';
import AppNotification from '../../helpers/AppNotification';
import AgentModel from '../../models/AgentModel';

function AgentManagePage() {
    const dispatch = useDispatch();
    const [agents, setAgents] = useState([]);
    const [users, setUsers] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [showBalanceModal, setShowBalanceModal] = useState(false);
    const [editingAgent, setEditingAgent] = useState(null);
    const [selectedAgent, setSelectedAgent] = useState(null);
    const [balanceAmount, setBalanceAmount] = useState('');
    const [loadError, setLoadError] = useState(null);
    const [formData, setFormData] = useState({
        user_id: '',
        domain: '',
        site_name: '',
        hotline: '',
        telegram: ''
    });

    useEffect(() => {
        loadAgents();
        loadAvailableUsers();
    }, []);

    const loadAgents = async () => {
        dispatch(setShowPreloader(true));
        setLoadError(null);
        try {
            const res = await AgentModel.getAll();
            if (res.success) {
                setAgents(res.data);
            } else {
                setLoadError('Lỗi: ' + (res.message || 'Không thể tải danh sách'));
            }
        } catch (error) {
            setLoadError('Lỗi: ' + (error.response?.data?.message || error.message));
        }
        dispatch(setShowPreloader(false));
    };

    const loadAvailableUsers = async () => {
        try {
            const res = await AgentModel.getAvailableUsers();
            if (res.success) {
                setUsers(res.data);
            }
        } catch (error) {
            console.error('Error loading users:', error);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        dispatch(setShowPreloader(true));
        try {
            let res;
            if (editingAgent) {
                res = await AgentModel.update(editingAgent.id, formData);
            } else {
                res = await AgentModel.create(formData);
            }
            if (res.success) {
                AppNotification.showSuccess(res.message || 'Thành công!');
                setShowModal(false);
                loadAgents();
                loadAvailableUsers();
                resetForm();
            } else {
                AppNotification.showError(res.message || 'Có lỗi!');
            }
        } catch (error) {
            AppNotification.showError(error.response?.data?.message || 'Có lỗi xảy ra!');
        }
        dispatch(setShowPreloader(false));
    };

    const handleAddBalance = async (e) => {
        e.preventDefault();
        if (!selectedAgent || !balanceAmount) return;
        
        dispatch(setShowPreloader(true));
        try {
            const res = await AgentModel.addBalance(selectedAgent.id, parseInt(balanceAmount));
            if (res.success) {
                AppNotification.showSuccess(res.message || 'Nạp tiền thành công!');
                setShowBalanceModal(false);
                setBalanceAmount('');
                loadAgents();
            } else {
                AppNotification.showError(res.message || 'Có lỗi!');
            }
        } catch (error) {
            AppNotification.showError(error.response?.data?.message || 'Có lỗi xảy ra!');
        }
        dispatch(setShowPreloader(false));
    };

    const handleDelete = async (id) => {
        const result = await Swal.fire({
            title: 'Xác nhận xóa?',
            text: 'Đại lý sẽ bị xóa và user sẽ về role thường',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Xóa',
            cancelButtonText: 'Hủy'
        });

        if (result.isConfirmed) {
            dispatch(setShowPreloader(true));
            try {
                const res = await AgentModel.delete(id);
                if (res.success) {
                    AppNotification.showSuccess('Đã xóa đại lý!');
                    loadAgents();
                    loadAvailableUsers();
                }
            } catch (error) {
                AppNotification.showError('Không thể xóa!');
            }
            dispatch(setShowPreloader(false));
        }
    };

    const resetForm = () => {
        setFormData({
            user_id: '',
            domain: '',
            site_name: '',
            hotline: '',
            telegram: ''
        });
        setEditingAgent(null);
    };

    const openEditModal = (agent) => {
        setEditingAgent(agent);
        setFormData({
            user_id: agent.user_id,
            domain: agent.domain,
            site_name: agent.site_name || '',
            hotline: agent.hotline || '',
            telegram: agent.telegram || ''
        });
        setShowModal(true);
    };

    const openBalanceModal = (agent) => {
        setSelectedAgent(agent);
        setBalanceAmount('');
        setShowBalanceModal(true);
    };

    const formatMoney = (num) => {
        return new Intl.NumberFormat('vi-VN').format(num || 0);
    };

    return (
        <MasterLayout>
            <ThemeBreadcrumb title="Quản lý Đại lý" />
            
            <div className="card">
                <div className="card-header d-flex justify-content-between align-items-center">
                    <h5 className="mb-0">Danh sách Đại lý ({agents.length})</h5>
                    <button className="btn btn-primary" onClick={() => { resetForm(); setShowModal(true); }}>
                        <i className="fas fa-plus me-2"></i>Thêm đại lý
                    </button>
                </div>
                <div className="card-body">
                    {loadError && (
                        <div className="alert alert-danger">{loadError}</div>
                    )}
                    
                    <div className="table-responsive">
                        <table className="table table-hover">
                            <thead>
                                <tr>
                                    <th>#</th>
                                    <th>User</th>
                                    <th>Domain</th>
                                    <th>Tên Website</th>
                                    <th>Số dư</th>
                                    <th>Trạng thái</th>
                                    <th>Thao tác</th>
                                </tr>
                            </thead>
                            <tbody>
                                {agents.length === 0 ? (
                                    <tr><td colSpan="7" className="text-center">Chưa có đại lý nào</td></tr>
                                ) : agents.map((agent, index) => (
                                    <tr key={agent.id}>
                                        <td>{index + 1}</td>
                                        <td>
                                            <strong>{agent.user?.user_name || agent.user?.name}</strong>
                                            <br/><small className="text-muted">{agent.user?.email}</small>
                                        </td>
                                        <td><code>{agent.domain}</code></td>
                                        <td>{agent.site_name || '-'}</td>
                                        <td><strong className="text-success">{formatMoney(agent.balance)} đ</strong></td>
                                        <td>
                                            <span className={`badge bg-${agent.is_active ? 'success' : 'danger'}`}>
                                                {agent.is_active ? 'Hoạt động' : 'Tắt'}
                                            </span>
                                        </td>
                                        <td>
                                            <button className="btn btn-sm btn-success me-1" onClick={() => openBalanceModal(agent)} title="Nạp tiền">
                                                <i className="fas fa-plus"></i>
                                            </button>
                                            <button className="btn btn-sm btn-warning me-1" onClick={() => openEditModal(agent)} title="Sửa">
                                                <i className="fas fa-edit"></i>
                                            </button>
                                            <button className="btn btn-sm btn-danger" onClick={() => handleDelete(agent.id)} title="Xóa">
                                                <i className="fas fa-trash"></i>
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            {/* Modal thêm/sửa đại lý */}
            {showModal && (
                <div className="modal show d-block" style={{backgroundColor: 'rgba(0,0,0,0.5)'}}>
                    <div className="modal-dialog">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">{editingAgent ? 'Sửa đại lý' : 'Thêm đại lý mới'}</h5>
                                <button type="button" className="btn-close" onClick={() => setShowModal(false)}></button>
                            </div>
                            <form onSubmit={handleSubmit}>
                                <div className="modal-body">
                                    {!editingAgent && (
                                        <div className="mb-3">
                                            <label className="form-label">Chọn User <span className="text-danger">*</span></label>
                                            <select className="form-select" value={formData.user_id} onChange={(e) => setFormData({...formData, user_id: e.target.value})} required>
                                                <option value="">-- Chọn user --</option>
                                                {users.map(u => (
                                                    <option key={u.id} value={u.id}>{u.user_name || u.name} ({u.email})</option>
                                                ))}
                                            </select>
                                            <small className="text-muted">Chỉ hiện users chưa là đại lý ({users.length} users)</small>
                                        </div>
                                    )}
                                    <div className="mb-3">
                                        <label className="form-label">Domain <span className="text-danger">*</span></label>
                                        <input type="text" className="form-control" value={formData.domain} onChange={(e) => setFormData({...formData, domain: e.target.value})} placeholder="vd: daily1.com" required disabled={!!editingAgent} />
                                    </div>
                                    <div className="mb-3">
                                        <label className="form-label">Tên Website</label>
                                        <input type="text" className="form-control" value={formData.site_name} onChange={(e) => setFormData({...formData, site_name: e.target.value})} placeholder="vd: Đại lý ABC" />
                                    </div>
                                    <div className="mb-3">
                                        <label className="form-label">Hotline</label>
                                        <input type="text" className="form-control" value={formData.hotline} onChange={(e) => setFormData({...formData, hotline: e.target.value})} placeholder="0909xxx" />
                                    </div>
                                    <div className="mb-3">
                                        <label className="form-label">Telegram</label>
                                        <input type="text" className="form-control" value={formData.telegram} onChange={(e) => setFormData({...formData, telegram: e.target.value})} placeholder="https://t.me/xxx" />
                                    </div>
                                </div>
                                <div className="modal-footer">
                                    <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>Hủy</button>
                                    <button type="submit" className="btn btn-primary">{editingAgent ? 'Cập nhật' : 'Thêm mới'}</button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal nạp tiền */}
            {showBalanceModal && selectedAgent && (
                <div className="modal show d-block" style={{backgroundColor: 'rgba(0,0,0,0.5)'}}>
                    <div className="modal-dialog">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Nạp tiền cho: {selectedAgent.user?.user_name}</h5>
                                <button type="button" className="btn-close" onClick={() => setShowBalanceModal(false)}></button>
                            </div>
                            <form onSubmit={handleAddBalance}>
                                <div className="modal-body">
                                    <div className="alert alert-info">
                                        Số dư hiện tại: <strong>{formatMoney(selectedAgent.balance)} đ</strong>
                                    </div>
                                    <div className="mb-3">
                                        <label className="form-label">Số tiền nạp <span className="text-danger">*</span></label>
                                        <input type="number" className="form-control" value={balanceAmount} onChange={(e) => setBalanceAmount(e.target.value)} placeholder="Nhập số tiền" min="1000" required />
                                    </div>
                                </div>
                                <div className="modal-footer">
                                    <button type="button" className="btn btn-secondary" onClick={() => setShowBalanceModal(false)}>Hủy</button>
                                    <button type="submit" className="btn btn-success">Nạp tiền</button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}
        </MasterLayout>
    );
}

export default AgentManagePage;
